import React, {useEffect, useState} from 'react';
import {App, Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag} from 'antd';
import {DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';
import {Permission, permissionService} from '../../services/permissionService';

const {Option} = Select;

const PermissionManagement: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
    const [form] = Form.useForm();
    const [permissionType, setPermissionType] = useState<string>('FUNCTION');

    useEffect(() => {
        loadPermissions();
    }, []);

    const loadPermissions = async () => {
        setLoading(true);
        try {
            const data = await permissionService.getAllPermissions();
            setPermissions(data);
        } catch (error) {
            messageApi.error('获取权限列表失败');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingPermission(null);
        setPermissionType('FUNCTION');
        form.resetFields();
        form.setFieldsValue({
            permissionType: 'FUNCTION',
            functionCode: undefined,
            resourceType: undefined
        });
        setModalVisible(true);
    };

    const handleEdit = (perm: Permission) => {
        setEditingPermission(perm);
        setPermissionType(perm.permissionType || 'FUNCTION');
        form.setFieldsValue({
            permissionCode: perm.permissionCode,
            permissionName: perm.permissionName,
            permissionType: perm.permissionType,
            functionCode: perm.functionCode,
            resourceType: perm.resourceType,
            parentPermissionId: perm.parentPermissionId,
            description: perm.description,
        });
        setModalVisible(true);
    };

    const handleDelete = async (permissionId: number) => {
        try {
            await permissionService.deletePermission(permissionId);
            messageApi.success('删除成功');
            loadPermissions();
        } catch (error: any) {
            messageApi.error(error.message || '删除失败');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editingPermission) {
                await permissionService.updatePermission(editingPermission.permissionId!, values);
                messageApi.success('更新成功');
            } else {
                await permissionService.createPermission(values);
                messageApi.success('创建成功');
            }
            setModalVisible(false);
            loadPermissions();
        } catch (error: any) {
            messageApi.error(error.message || '操作失败');
        }
    };

    const handlePermissionTypeChange = (value: string) => {
        setPermissionType(value);
        if (value === 'FUNCTION') {
            form.setFieldsValue({resourceType: undefined, functionCode: undefined});
        } else {
            form.setFieldsValue({functionCode: undefined, resourceType: undefined});
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'permissionId',
            key: 'permissionId',
            width: 60,
        },
        {
            title: '权限代码',
            dataIndex: 'permissionCode',
            key: 'permissionCode',
        },
        {
            title: '权限名称',
            dataIndex: 'permissionName',
            key: 'permissionName',
        },
        {
            title: '类型',
            dataIndex: 'permissionType',
            key: 'permissionType',
            render: (type: string) => (
                <Tag color={type === 'FUNCTION' ? 'purple' : 'orange'}>
                    {type === 'FUNCTION' ? '功能' : '资源'}
                </Tag>
            ),
        },
        {
            title: '操作',
            key: 'action',
            render: (_: any, record: Permission) => {
                // 初始化权限不显示操作按钮
                if (record.isInitialized) {
                    return null;
                }
                return (
                    <Space>
                        <Button type="link" icon={<EditOutlined/>} onClick={() => handleEdit(record)}>
                            编辑
                        </Button>
                        <Popconfirm title="确定删除该权限吗？" onConfirm={() => handleDelete(record.permissionId!)}>
                            <Button type="link" danger icon={<DeleteOutlined/>}>删除</Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <div>
            <div style={{marginBottom: 16, textAlign: 'right'}}>
                <Button type="primary" icon={<PlusOutlined/>} onClick={handleCreate}>
                    {/*创建权限*/}
                </Button>
            </div>
            <Table
                columns={columns}
                dataSource={permissions}
                rowKey="permissionId"
                loading={loading}
                pagination={false}
            />

            <Modal
                title={editingPermission ? '编辑权限' : '创建权限'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="permissionCode" label="权限代码"
                               rules={[{required: true, message: '请输入权限代码'}]}>
                        <Input disabled={!!editingPermission}/>
                    </Form.Item>
                    <Form.Item name="permissionName" label="权限名称"
                               rules={[{required: true, message: '请输入权限名称'}]}>
                        <Input/>
                    </Form.Item>

                    {/* 权限类型 - 编辑时禁用 */}
                    <Form.Item name="permissionType" label="权限类型">
                        <Select
                            disabled={!!editingPermission}
                            onChange={handlePermissionTypeChange}
                        >
                            <Option value="FUNCTION">功能权限</Option>
                            <Option value="RESOURCE">资源权限</Option>
                        </Select>
                    </Form.Item>

                    {/* 功能代码 - 仅功能权限显示 */}
                    {permissionType === 'FUNCTION' && (
                        <Form.Item
                            name="functionCode"
                            label="功能代码"
                            rules={[{required: true, message: '功能权限必须填写功能代码'}]}
                        >
                            <Input
                                placeholder="如: TRADE, USER_ADMIN"
                                disabled={!!editingPermission}
                            />
                        </Form.Item>
                    )}

                    {/* 资源类型 - 仅资源权限显示 */}
                    {permissionType === 'RESOURCE' && (
                        <Form.Item
                            name="resourceType"
                            label="资源类型"
                            rules={[{required: true, message: '资源权限必须填写资源类型'}]}
                        >
                            <Input
                                placeholder="如: t_cex_key, position"
                                disabled={!!editingPermission}
                            />
                        </Form.Item>
                    )}

                    <Form.Item name="parentPermissionId" label="父级权限">
                        <Select allowClear placeholder="选择父级权限">
                            {permissions
                                .filter(p => p.permissionId !== editingPermission?.permissionId)
                                .map(p => (
                                    <Option key={p.permissionId} value={p.permissionId!}>
                                        {p.permissionName}
                                    </Option>
                                ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="description" label="描述">
                        <Input.TextArea/>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PermissionManagement;